import logger from "../logger";
import { isSyncData } from "../types";
import { findAndLoadAll } from "./";
const PAGINATION_SIZE = 1000;
export class NotPresentError extends Error {
}
class LocalCacheImpl {
    app;
    protocolTypes = {};
    protocolContentTypes = {};
    checklistTypes = {};
    users = {};
    roles = {};
    books = {};
    protocolTypeGroups = {};
    equipmentLists = {};
    equipmentItems = {};
    soundingSpots = {};
    etmalCalculations = {};
    views = {};
    constructor(app) {
        this.app = app;
    }
    filterDeleted(entities, includeDeleted) {
        return entities.filter((entity) => includeDeleted || (entity.successor === undefined && !entity.meta.deleted));
    }
    getProtocolTypes(includeDeleted) {
        return this.filterDeleted(Object.values(this.protocolTypes), includeDeleted);
    }
    getProtocolType(id) {
        const protocolType = this.protocolTypes[id];
        if (!protocolType) {
            throw new NotPresentError(`ProtocolType with id '${id}' is not present`);
        }
        return protocolType;
    }
    getOptionalProtocolType(id) {
        try {
            return this.getProtocolType(id);
        }
        catch (error) {
            return undefined;
        }
    }
    getProtocolContentTypes(includeDeleted) {
        return this.filterDeleted(Object.values(this.protocolContentTypes), includeDeleted);
    }
    getProtocolContentType(id) {
        const protocolContentType = this.protocolContentTypes[id];
        if (!protocolContentType) {
            throw new NotPresentError(`ProtocolContentType with id '${id}' is not present`);
        }
        return protocolContentType;
    }
    getOptionalProtocolContentType(id) {
        try {
            return this.getProtocolContentType(id);
        }
        catch (error) {
            return undefined;
        }
    }
    getChecklistTypes(includeDeleted) {
        return this.filterDeleted(Object.values(this.checklistTypes), includeDeleted);
    }
    getChecklistType(id) {
        const checklistType = this.checklistTypes[id];
        if (!checklistType) {
            throw new NotPresentError(`ChecklistType with id '${id}' is not present`);
        }
        return checklistType;
    }
    getUsers(includeDeleted) {
        return Object.values(this.users).filter((user) => includeDeleted || user.meta.deleted === null);
    }
    getUserById(id) {
        const user = this.users[id];
        if (!user) {
            throw new NotPresentError(`User with id '${id}' is not present`);
        }
        return user;
    }
    getUser(userName) {
        let user = this.getUsers().find((user) => user.userName === userName);
        if (!user) {
            user = this.getUsers(true).find((user) => user.userName === userName);
        }
        if (!user) {
            throw new NotPresentError(`User '${userName}' is not present`);
        }
        return user;
    }
    getRoles(includeDeleted) {
        return Object.values(this.roles).filter((role) => includeDeleted || role.meta.deleted === null);
    }
    getRoleById(id) {
        const role = this.roles[id];
        if (!role) {
            throw new NotPresentError(`Role with id '${id}' is not present`);
        }
        return role;
    }
    getRole(name) {
        let role = this.getRoles().find((role) => role.name === name);
        if (!role) {
            role = this.getRoles(true).find((role) => role.name === name);
        }
        if (!role) {
            throw new NotPresentError(`Role '${name}' is not present`);
        }
        return role;
    }
    getBooks(includeDeleted) {
        return Object.values(this.books).filter((book) => includeDeleted || book.meta.deleted === null);
    }
    getBookById(id) {
        const book = this.books[id];
        if (!book) {
            throw new NotPresentError(`Book with id '${id}' is not present`);
        }
        return book;
    }
    getBook(technicalName) {
        let book = this.getBooks().find((book) => book.technicalName === technicalName);
        if (!book) {
            book = this.getBooks(true).find((book) => book.technicalName === technicalName);
        }
        if (!book) {
            throw new NotPresentError(`Book with technical name '${technicalName}' is not present`);
        }
        return book;
    }
    getProtocolTypeGroups() {
        return Object.values(this.protocolTypeGroups);
    }
    getProtocolTypeGroup(id) {
        const protocolTypeGroup = this.protocolTypeGroups[id];
        if (!protocolTypeGroup) {
            throw new NotPresentError(`ProtocolTypeGroup with id '${id}' is not present`);
        }
        return protocolTypeGroup;
    }
    getEquipmentLists(includeDeleted) {
        return this.filterDeleted(Object.values(this.equipmentLists), includeDeleted);
    }
    getEquipmentList(id) {
        const equipmentList = this.equipmentLists[id];
        if (!equipmentList) {
            throw new NotPresentError(`EquipmentList with id '${id}' is not present`);
        }
        return equipmentList;
    }
    getEquipmentItems(includeDeleted) {
        return Object.values(this.equipmentItems).filter((equipmentItem) => includeDeleted || equipmentItem.meta.deleted === null);
    }
    getEquipmentItem(id) {
        const equipmentItem = this.equipmentItems[id];
        if (!equipmentItem) {
            throw new NotPresentError(`EquipmentItem with id '${id}' is not present`);
        }
        return equipmentItem;
    }
    getSoundingSpots(includeDeleted) {
        return Object.values(this.soundingSpots).filter((soundingSpot) => includeDeleted || soundingSpot.meta.deleted === null);
    }
    getSoundingSpot(id) {
        const soundingSpot = this.soundingSpots[id];
        if (!soundingSpot) {
            throw new NotPresentError(`SoundingSpot with id '${id}' is not present`);
        }
        return soundingSpot;
    }
    getEtmalCalculations(includeDeleted) {
        return Object.values(this.etmalCalculations).filter((etmalCalculation) => includeDeleted || etmalCalculation.meta.deleted === null);
    }
    getEtmalCalculation(id) {
        const etmalCalculation = this.etmalCalculations[id];
        if (!etmalCalculation) {
            throw new NotPresentError(`EtmalCalculation with id '${id}' is not present`);
        }
        return etmalCalculation;
    }
    getViews(includeDeleted) {
        return this.filterDeleted(Object.values(this.views), includeDeleted);
    }
    getView(id) {
        const view = this.views[id];
        if (!view) {
            throw new NotPresentError(`View with id '${id}' is not present`);
        }
        return view;
    }
    updateDataHandler(cacheMap, data) {
        if (!Array.isArray(data)) {
            data = [data];
        }
        data.forEach((singleData) => {
            if (!isSyncData(singleData)) {
                cacheMap[singleData._id] = singleData;
            }
        });
    }
    removeDataHandler(cacheMap, data) {
        if (!Array.isArray(data)) {
            data = [data];
        }
        data.forEach((singleData) => {
            if (!isSyncData(singleData)) {
                delete cacheMap[singleData._id];
            }
        });
    }
    async initDataCache(serviceName, cacheMap, options = {}) {
        const { listen, disableSoftDelete } = options;
        logger.info('Initializing local cache for %s. Listening? %s', serviceName, listen);
        if (listen === true) {
            this.app.service(serviceName).on('created', (data) => {
                this.updateDataHandler(cacheMap, data);
                logger.debug('Updated cache %s by created event. Current state is %s items', serviceName, Object.keys(cacheMap).length, data);
            });
            this.app.service(serviceName).on('updated', (data) => {
                this.updateDataHandler(cacheMap, data);
                logger.debug('Updated cache %s by updated event. Current state is %s items', serviceName, Object.keys(cacheMap).length, data);
            });
            this.app.service(serviceName).on('patched', (data) => {
                this.updateDataHandler(cacheMap, data);
                logger.debug('Updated cache %s by patched event. Current state is %s items', serviceName, Object.keys(cacheMap).length, data);
            });
            this.app.service(serviceName).on('removed', (data) => {
                this.removeDataHandler(cacheMap, data);
                logger.debug('Updated cache %s by removed event. Current state is %s items', serviceName, Object.keys(cacheMap).length, data);
            });
        }
        await findAndLoadAll(this.app, serviceName, (chunk) => {
            this.updateDataHandler(cacheMap, chunk);
            logger.info('Still Initializing cache %s. Current state is %s items', serviceName, Object.keys(cacheMap).length);
        }, { disableSoftDelete, paginationSize: PAGINATION_SIZE });
        logger.info('Initialized cache %s with %s items', serviceName, Object.keys(cacheMap).length);
        this.initialized[serviceName] = true;
        this.initializedAwaiter[serviceName]?.forEach((resolve) => resolve());
        this.initializedAwaiter[serviceName] = undefined;
    }
    async fill(listen) {
        await this.initDataCache('role', this.roles, { listen, disableSoftDelete: true });
        await this.initDataCache('user', this.users, { listen, disableSoftDelete: true });
        await Promise.all([
            this.initDataCache('protocolType', this.protocolTypes, { disableSoftDelete: true, listen }),
            this.initDataCache('protocolContentType', this.protocolContentTypes, {
                disableSoftDelete: true,
                listen,
            }),
            this.initDataCache('checklistType', this.checklistTypes, { disableSoftDelete: true, listen }),
            this.initDataCache('book', this.books, { disableSoftDelete: true, listen }),
            this.initDataCache('equipmentList', this.equipmentLists, { disableSoftDelete: true, listen }),
            this.initDataCache('equipmentItem', this.equipmentItems, { listen }),
            this.initDataCache('sounding-spot', this.soundingSpots, { disableSoftDelete: true, listen }),
            this.initDataCache('protocolTypeGroup', this.protocolTypeGroups, { listen }),
            this.initDataCache('view', this.views, { listen }),
        ]);
    }
    clear() {
        this.protocolTypes = {};
        this.protocolContentTypes = {};
        this.checklistTypes = {};
        this.roles = {};
        this.books = {};
        this.protocolTypeGroups = {};
        this.users = {};
        this.equipmentLists = {};
        this.equipmentItems = {};
        this.initialized = {
            user: false,
            role: false,
            book: false,
            equipmentItem: false,
            equipmentList: false,
            protocolContentType: false,
            protocolType: false,
            protocolTypeGroup: false,
            'sounding-spot': false,
            checklistType: false,
            view: false,
        };
    }
    initialized = {
        user: false,
        role: false,
        book: false,
        equipmentItem: false,
        equipmentList: false,
        protocolContentType: false,
        protocolType: false,
        protocolTypeGroup: false,
        'sounding-spot': false,
        checklistType: false,
        view: false,
    };
    initializedAwaiter = {};
    async init() {
        this.clear();
        await this.fill(true);
        this.initializedAwaiter.all?.forEach((resolve) => resolve());
        this.initializedAwaiter.all = undefined;
    }
    async update() {
        await this.fill(false);
    }
    async waitForInitialization(entity) {
        if ((entity && this.initialized[entity]) || Object.values(this.initialized).every((initialized) => initialized)) {
            return;
        }
        const key = entity || 'all';
        await new Promise((resolve) => {
            let awaiters = this.initializedAwaiter[key];
            if (!awaiters) {
                awaiters = [];
                this.initializedAwaiter[key] = awaiters;
            }
            awaiters.push(resolve);
        });
    }
}
export function init(app) {
    const localCache = new LocalCacheImpl(app);
    app.set('localCache', localCache);
}
//# sourceMappingURL=LocalCache.js.map